import {
  enumLoadStatus,
  enumPurchaseLoadSortOption,
  LoadStatus,
  PurchaseLoadStatus,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { purchaseLoadCompleteInventoryAtom } from '../_graphql/mutationPurchaseLoadCompleteInventory';
import { purchaseLoadCompletePriceAtom } from '../_graphql/mutationPurchaseLoadCompletePrice';
import { purchaseLoadHoldAtom } from '../_graphql/mutationPurchaseLoadHold';
import { purchaseLoadPayAtom } from '../_graphql/mutationPurchaseLoadPay';
import { purchaseLoadReleaseAtom } from '../_graphql/mutationPurchaseLoadRelease';
import { purchaseLoadRevertStatusAtom } from '../_graphql/mutationPurchaseLoadRevertStatus';
import { purchaseLoadUpdateAtom } from '../_graphql/mutationPurchaseLoadUpdate';
import { purchaseLoadVoidAtom } from '../_graphql/mutationPurchaseLoadVoid';
import { accountsQueryAtom } from '../_graphql/queryAccounts';
import { deductionTypesQueryAtom } from '../_graphql/queryDeductionTypes';
import { loadDetailQueryAtom } from '../_graphql/queryLoadDetail';
import { loadsListQueryAtom } from '../_graphql/queryLoadsList';
import { purchaseLoadDetailQueryAtom } from '../_graphql/queryPurchaseLoadDetail';
import { purchaseLoadsListQueryAtom } from '../_graphql/queryPurchaseLoadsList';
import { tradeRoutePaths as tradeRoutes } from '../routes';
import {
  type LayoutViewMode,
  type ScaleTicketTab,
  type TicketDirection,
  type TicketDocumentSelection,
  type TicketListFilter,
} from '../types/scaleTickets';

import { resolveItemPriceQueryAtom } from '~/modules/pricing/_graphql/queryResolveProductPrice';
import { $facilityId } from '~/shared/providers/auth/identity.localState';
import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

// 🔘🔘🔘 PLAIN ATOMS
export const $inboundMineOnly = atomWithStorage<boolean>(
  'scaleTicket.inbound.mineOnly',
  false,
);
export const $mineOnly = $inboundMineOnly;

export const $footerHeavyWeight = atom<Weight | null>(null);
export const $footerLightWeight = atom<Weight | null>(null);
export const $footerIsSaving = atom<boolean>(false);

export const $viewMode = atomWithStorage<LayoutViewMode>(
  'scaleTicket.inbound.viewMode',
  'grid',
);

export const $selectedDocuments = atom<TicketDocumentSelection>({
  BillOfLading: false,
  BookingInvoice: false,
  Invoice: false,
  LoadInvoice: false,
  PackingList: false,
  PurchaseOrder: false,
  SalesOrder: false,
  WeightTicket: false,
  OutboundScaleTicket: false,
  ThermalWeightTicket: false,
  FullPageWeightTicket: false,
  ThermalScaleTicket: false,
  FullPageScaleTicket: false,
  ShippingSummary: false,
});

export const $resetSelectedDocuments = atom(null, (_get, set) => {
  set($selectedDocuments, {
    BillOfLading: false,
    BookingInvoice: false,
    Invoice: false,
    LoadInvoice: false,
    PackingList: false,
    PurchaseOrder: false,
    SalesOrder: false,
    WeightTicket: false,
    OutboundScaleTicket: false,
    ThermalWeightTicket: false,
    FullPageWeightTicket: false,
    ThermalScaleTicket: false,
    FullPageScaleTicket: false,
    ShippingSummary: false,
  });
});

export const $ticketDirection = atomWithStorage<TicketDirection>(
  'scaleTicketPro.direction',
  'inbound',
);

// 🟣🟣🟣 PARAMS
export const $scaleTicketPathParams = atomFromRoutePath(
  tradeRoutes.scaleTicketDetail,
);
export const $scaleTicketId = atom((get) => {
  const params = get($scaleTicketPathParams);
  return params?.id ? String(params.id) : undefined;
});

export const $scaleTicketTab =
  atomFromQueryString.string<ScaleTicketTab>('tab');

// 🟡🟡🟡 FILTERS -> inbound ticket list
export const $inboundTicketFilter = atom<TicketListFilter>({
  statuses: ['Open', 'Held'],
  searchTerm: '',
  accountIds: [],
});

// 🟡🟡🟡 FILTERS -> outbound ticket list
export const $outboundTicketFilter = atom<TicketListFilter>({
  statuses: [enumLoadStatus.Unassigned, enumLoadStatus.Pending],
  searchTerm: '',
  accountIds: [],
});

// 🔵🔵🔵 COMPUTED DATA -> whether the current ticket is in create (new) mode
export const $isScaleTicketCreateMode = atom((get) => {
  const id = get($scaleTicketId);
  return id === 'new';
});

// 🟢🟢🟢 REMOTE DATA -> inbound ticket detail query
export const $scaleTicketDetail = purchaseLoadDetailQueryAtom((get) => {
  const ticketId = get($scaleTicketId);
  const isCreateMode = get($isScaleTicketCreateMode);
  const direction = get($ticketDirection);
  return {
    variables: { id: ticketId ?? '' },
    skip: !ticketId || isCreateMode || direction !== 'inbound',
  };
});

// 🟠🟠🟠 ACTION -> HOLD inbound ticket
export const $holdScaleTicket = purchaseLoadHoldAtom({
  onSuccess: (get) => {
    get($scaleTicketDetail).refetch();
  },
});

// 🟠🟠🟠 ACTION -> RELEASE inbound ticket from hold
export const $releaseScaleTicket = purchaseLoadReleaseAtom({
  onSuccess: (get) => {
    get($scaleTicketDetail).refetch();
  },
});

// 🟠🟠🟠 ACTION -> COMPLETE INVENTORY for inbound ticket
export const $completeInventoryScaleTicket = purchaseLoadCompleteInventoryAtom({
  onSuccess: (get) => {
    get($scaleTicketDetail).refetch();
  },
});

// 🟠🟠🟠 ACTION -> COMPLETE PRICE for inbound ticket
export const $completePriceScaleTicket = purchaseLoadCompletePriceAtom({
  onSuccess: (get) => {
    get($scaleTicketDetail).refetch();
  },
});

// 🟠🟠🟠 ACTION -> PAY inbound ticket
export const $payScaleTicket = purchaseLoadPayAtom({
  onSuccess: (get) => {
    get($scaleTicketDetail).refetch();
  },
});

// 🟠🟠🟠 ACTION -> REVERT STATUS for inbound ticket
export const $revertScaleTicket = purchaseLoadRevertStatusAtom({
  onSuccess: (get) => {
    get($scaleTicketDetail).refetch();
  },
});

// 🟠🟠🟠 ACTION -> VOID inbound ticket
export const $voidScaleTicket = purchaseLoadVoidAtom({
  onSuccess: (get) => {
    get($scaleTicketDetail).refetch();
  },
});

export const $updatePurchaseLoadFromModal = purchaseLoadUpdateAtom({
  onSuccess: (get) => {
    get($scaleTicketDetail).refetch();
  },
});

// 🔘🔘🔘 PLAIN ATOMS -> active outbound ticket tab (persists across navigation)
export const $outboundTicketTab = atom<string>('materials');

// 🟢🟢🟢 REMOTE DATA -> outbound ticket detail query
export const $outboundTicketDetail = loadDetailQueryAtom((get) => {
  const ticketId = get($scaleTicketId);
  const direction = get($ticketDirection);
  const isCreateMode = get($isScaleTicketCreateMode);
  return {
    variables: { id: ticketId ?? '' },
    skip: !ticketId || direction !== 'outbound' || isCreateMode,
  };
});

// 🟢🟢🟢 REMOTE DATA -> paginated list of inbound tickets matching the current filter
export const $inboundTicketLoads = purchaseLoadsListQueryAtom((get) => {
  const filter = get($inboundTicketFilter);
  const globalFacilityId = get($facilityId);
  return {
    variables: {
      status: filter.statuses as PurchaseLoadStatus[],
      searchTerm: filter.searchTerm || undefined,
      facilityId: globalFacilityId || undefined,
      supplierAccountIds: filter.accountIds.length
        ? filter.accountIds
        : undefined,
      sorting: [enumPurchaseLoadSortOption.CreatedAtDesc],
      limit: 50,
      offset: 0,
    },
    skip: false,
  };
});

// 🟢🟢🟢 REMOTE DATA -> paginated list of outbound loads matching the current filter
export const $outboundLoads = loadsListQueryAtom((get) => {
  const filter = get($outboundTicketFilter);
  const globalFacilityId = get($facilityId);

  return {
    variables: {
      status: filter.statuses as LoadStatus[],
      searchTerm: filter.searchTerm || undefined,
      facilityId: globalFacilityId || undefined,
      accountIds: filter.accountIds.length ? filter.accountIds : undefined,
      limit: 50,
      offset: 0,
    },
    skip: false,
  };
});

// 🟢🟢🟢 REMOTE DATA -> all accounts for filter popovers
export const $filterAccounts = accountsQueryAtom(() => ({
  variables: {
    searchTerm: '',
    limit: 100,
    offset: 0,
  },
}));

// 🟢🟢🟢 REMOTE DATA -> all deduction types for line item selectors
export const $deductionTypes = deductionTypesQueryAtom();

export const $lazyResolveItemPrice = resolveItemPriceQueryAtom(() => ({
  variables: {} as never,
  lazy: true,
}));
